import { useEffect, useRef, useState, type FormEvent } from 'react'
import { Link, useParams } from 'react-router'
import { Container } from '@carouselgg/nexus/container'
import { Text } from '@carouselgg/nexus/text'
import { Button } from '@carouselgg/nexus/button'
import { Callout } from '@carouselgg/nexus/callout'
import { ArrowLeft, Send } from '@carouselgg/nexus/icon'
import { useAuthStore } from '@carouselgg/auth/store'
import { ApiError } from '@carouselgg/auth/client'
import { getContactThread, replyToContactThread, type ContactThread } from '@carouselgg/auth/contact'
import styles from './MessageThreadPage.module.css'

const MAX_LENGTH = 2000

/**
 * One conversation with a team's contact, reached from a message notification.
 * Leagues and clubs write here instead of to a Discord, email or phone —
 * always for a player under 18, whose details they never see.
 */
export function MessageThreadPage() {
  const { threadId = '' } = useParams<{ threadId: string }>()
  const isMinor = useAuthStore((s) => s.isMinor)
  const [thread, setThread] = useState<ContactThread | null>(null)
  const [loadError, setLoadError] = useState('')
  const [reply, setReply] = useState('')
  const [sending, setSending] = useState(false)
  const [sendError, setSendError] = useState('')
  const endRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    let live = true
    setThread(null)
    setLoadError('')
    getContactThread(threadId)
      .then((t) => { if (live) setThread(t) })
      .catch((err: unknown) => {
        if (!live) return
        setLoadError(err instanceof ApiError && err.status === 404
          ? "This conversation doesn't exist or isn't yours."
          : err instanceof Error ? err.message : 'Could not load this conversation.')
      })
    return () => { live = false }
  }, [threadId])

  useEffect(() => { endRef.current?.scrollIntoView?.({ block: 'end' }) }, [thread?.messages.length])

  const send = async (e: FormEvent) => {
    e.preventDefault()
    const body = reply.trim()
    if (!body) return
    setSending(true)
    setSendError('')
    try {
      setThread(await replyToContactThread(threadId, body))
      setReply('')
    } catch (err) {
      setSendError(err instanceof Error ? err.message : 'Could not send your reply.')
    } finally {
      setSending(false)
    }
  }

  return (
    <Container size="sm">
      <div className={styles.page}>
        <Link to="/notifications" className={styles.back}>
          <ArrowLeft size={14} />
          <span>Notifications</span>
        </Link>

        {loadError ? (
          <Callout variant="error">{loadError}</Callout>
        ) : !thread ? (
          <Text muted>Loading...</Text>
        ) : (
          <>
            <div className={styles.header}>
              <Text as="h1" size="xl" weight="bold">{thread.team_name}</Text>
              <Text size="sm" muted>Messages with {thread.recipient_name}</Text>
            </div>

            <ol className={styles.messages} aria-label="Messages">
              {thread.messages.map((m) => (
                <li key={m.id} className={`${styles.message} ${m.mine ? styles.mine : ''}`}>
                  <div className={styles.meta}>
                    <span className={styles.sender}>{m.mine ? 'You' : m.sender_name}</span>
                    <time dateTime={m.created_at}>
                      {new Date(m.created_at).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                    </time>
                  </div>
                  <p className={styles.body}>{m.body}</p>
                </li>
              ))}
              <div ref={endRef} />
            </ol>

            <form onSubmit={(e) => void send(e)} className={styles.form}>
              <label htmlFor="thread-reply" className={styles.label}>Reply</label>
              <textarea
                id="thread-reply"
                className={styles.textarea}
                value={reply}
                maxLength={MAX_LENGTH}
                rows={3}
                onChange={(e) => setReply(e.target.value)}
                placeholder="Write a reply"
              />
              {isMinor && <Text size="xs" muted>Reply here — your contact details aren't shared.</Text>}
              {sendError && <Callout variant="error">{sendError}</Callout>}
              <div className={styles.actions}>
                <Button type="submit" variant="primary" size="sm" disabled={sending || !reply.trim()}>
                  <Send size={14} />
                  {sending ? 'Sending...' : 'Send reply'}
                </Button>
              </div>
            </form>
          </>
        )}
      </div>
    </Container>
  )
}
